import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, ApiError } from "@/lib/api";
import { formatCents } from "@/lib/format";
import type { ResultadoExtratoClt } from "@/data/folha";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, FileCheck2, Loader2, Upload } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  folhaId: string;
  onSuccess: () => void;
}

/**
 * Importa o "Extrato Mensal" (.xls) da contabilidade: desde 04/11/2026 é ele o
 * holerite dos CLT (o cálculo pelo sistema foi desligado). O arquivo é o mês
 * inteiro e substitui o extrato anterior da competência.
 */
export const ImportarExtratoCltModal = ({ open, onClose, folhaId, onSuccess }: Props) => {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<ResultadoExtratoClt | null>(null);

  const handleClose = () => {
    setArquivo(null);
    setResultado(null);
    onClose();
  };

  const enviar = async (force = false) => {
    if (!arquivo) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("arquivo", arquivo);
      const res = await api.postForm<ResultadoExtratoClt>(
        `/folhas/${folhaId}/importar/extrato-clt${force ? "?force=true" : ""}`,
        fd,
      );
      setResultado(res);
      onSuccess();
      if (res.naoCasados === 0 && res.divergentes.length === 0) {
        toast.success(`${res.casados} holerite(s) importado(s)`);
      } else {
        toast.warning(`${res.casados} importado(s) — confira os avisos`);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: { code?: string; message?: string } } | undefined;
        if (body?.error?.code === "ARQUIVO_JA_IMPORTADO") {
          toast.warning(body.error.message ?? "Arquivo já importado", {
            action: { label: "Importar mesmo assim", onClick: () => void enviar(true) },
            duration: 10_000,
          });
          return;
        }
      }
      toast.error(err instanceof Error ? err.message : "Erro ao importar o extrato", {
        duration: 12_000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Importar o extrato da contabilidade</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-1.5">
              <p>
                O holerite dos CLT é o da contabilidade. Envie o <strong>Extrato Mensal</strong>{" "}
                (.xls) desta competência: os holerites aparecem na aba CLT e entram na exportação,
                no ERP e no dashboard.
              </p>
              <p>
                Admissão, cargo, dependentes e gratificação que ainda faltam no cadastro são
                completados pelo extrato (nada que o RH já preencheu é sobrescrito). Substitui o
                extrato anterior desta competência e apaga os holerites que o sistema tinha
                calculado nela (mensal, férias e 13º).
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <label
            className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-8 text-sm transition-colors
              ${arquivo ? "border-primary/40 bg-primary/5" : "border-input bg-muted/30 hover:border-primary/50 hover:bg-primary/5"}`}
          >
            {arquivo ? (
              <>
                <FileCheck2 className="h-8 w-8 text-primary" />
                <span className="font-medium text-primary">{arquivo.name}</span>
                <span className="text-xs text-muted-foreground">Clique para trocar</span>
              </>
            ) : (
              <>
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="font-medium">Clique para selecionar o extrato</span>
                <span className="text-xs text-muted-foreground">EXTRATO_FOLHA_MM-AAAA.xls</span>
              </>
            )}
            <input
              type="file"
              accept=".xls,.xlsx"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setArquivo(f);
                  setResultado(null);
                }
                e.target.value = "";
              }}
            />
          </label>

          {resultado && (
            <div className="space-y-2 rounded-md border p-3 text-sm">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                <span>
                  {resultado.empregados} empregado(s) · {resultado.casados} associado(s) · líquido
                  total {formatCents(resultado.conferencia.totalLiquido)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Bate com o Líquido Geral do extrato ({resultado.empresaNome}).
              </p>

              {resultado.contratosCompletados > 0 && (
                <p className="text-xs text-muted-foreground">
                  {resultado.contratosCompletados} cadastro(s) CLT completado(s) com dados do extrato.
                </p>
              )}

              {resultado.vinculosAtualizados.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {resultado.vinculosAtualizados.length} cadastro(s) casado(s) pelo CPF passaram a
                  CLT: {resultado.vinculosAtualizados.map((v) => v.nome).join(", ")}.
                </p>
              )}

              {resultado.naoCasados > 0 && (
                <div className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    {resultado.naoCasados} empregado(s) sem cadastro correspondente. Associe na aba{" "}
                    <strong>Pendências</strong> — sem isso o holerite não chega ao empregado e a
                    competência não fecha.
                  </span>
                </div>
              )}

              {resultado.divergentes.length > 0 && (
                <div className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
                  <p className="font-medium">
                    {resultado.divergentes.length} empregado(s) do extrato com rubricas que não batem
                    com os totais dele (só informativo):
                  </p>
                  <ul className="list-disc space-y-0.5 pl-4">
                    {resultado.divergentes.slice(0, 5).map((d) => (
                      <li key={d.linha}>
                        {d.nome}: {d.detalhes.join(" ")}
                      </li>
                    ))}
                    {resultado.divergentes.length > 5 && (
                      <li>e mais {resultado.divergentes.length - 5}</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose}>
              {resultado ? "Fechar" : "Cancelar"}
            </Button>
            <Button onClick={() => void enviar()} disabled={!arquivo || loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {loading ? "Processando..." : "Importar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
